import { ROLE } from './common';

/** Poll GET /notifications/count while logged in (footer badge + activity list sync) */
export const NOTIFICATION_COUNT_POLL_INTERVAL_MS = 4 * 60 * 1000;

/** When true, activity tiles are hidden and an empty state is shown. */
export const MESSAGE_TABS_BY_ROLE = {
  /** Monthly creator — matches /notifications/... active */
  1: [
    { id: 'all', label: 'All', count: null },
    { id: 'priority_messages', label: 'Priority Messages', count: 0 },
    { id: 'sales_tips', label: 'Sales & Tips', count: 0 },
    { id: 'subscribers', label: 'Subscribers', count: 0 },
    { id: 'payouts', label: 'Payouts', count: 0 },
    { id: 'tags', label: 'Tags', count: 0 },
    { id: 'promotion_campaign', label: 'Promotion Campaign', count: null },
    { id: 'archived', label: 'Archived', count: 0 },
  ],
  /** Subscriber (fan) — matches /notifications/... active + merged archive */
  2: [
    { id: 'all', label: 'All', count: null },
    { id: 'priority_messages', label: 'Priority Messages', count: 0 },
    { id: 'new_posts', label: 'New Posts', count: 0 },
    { id: 'subscriptions', label: 'Subscriptions', count: 0 },
    { id: 'purchases_tips', label: 'Purchases & Tips', count: 0 },
    { id: 'promotions', label: 'Promotions', count: null },
    { id: 'archived', label: 'Archived', count: 0 },
  ],
  /** PPV creator — same notification APIs as monthly creator except no promotions tab */
  3: [
    { id: 'all', label: 'All', count: null },
    { id: 'priority_messages', label: 'Priority Messages', count: 0 },
    { id: 'sales_tips', label: 'Sales & Tips', count: 0 },
    { id: 'followers', label: 'Followers', count: 0 },
    { id: 'payouts', label: 'Payouts', count: 0 },
    { id: 'tags', label: 'Tags', count: 0 },
    { id: 'archived', label: 'Archived', count: 0 },
  ],
};

export const ACTIVITY_PAGE_LIMIT = 10;

/** Tab ids that request `grouped=false` (active + archived promotion endpoints). */
export const ACTIVITY_PROMOTION_TAB_IDS = new Set([
  'promotions',
  'promotion_campaign',
  'payouts',
]);

/**
 * @param {string} tabId
 * @returns {boolean}
 */
export function getActivityGroupedForTab(tabId) {
  return !ACTIVITY_PROMOTION_TAB_IDS.has(tabId);
}

/** Redux byGrouped key for archived notification slices (per category tab). */
export const ACTIVITY_ARCHIVED_SLICE_KEY = 'archived';

/**
 * @param {{ grouped?: boolean, archived?: boolean }} options
 * @returns {string}
 */
export function getActivitySliceKey({ grouped = true, archived = false } = {}) {
  if (archived) return ACTIVITY_ARCHIVED_SLICE_KEY;
  return String(!!grouped);
}

/** Category tab ids shown as sections on the Archived tab (same as All, minus archive meta-tab). */
export function getArchivedActivitySectionTabIds(role) {
  return getFetchableActivityTabIds(role).filter((id) => id !== 'archived');
}

const CREATOR_SUB_ACTIVITY_TABS = Object.freeze([
  'priority_messages',
  'sales_tips',
  'subscribers',
  'payouts',
  'tags',
  'promotion_campaign',
  'archived',
]);

const CREATOR_CONTENT_ACTIVITY_TABS = Object.freeze([
  'priority_messages',
  'sales_tips',
  'followers',
  'payouts',
  'tags',
  'archived',
]);

/** Subscriber — matches /api/v1/notifications/... */
const USER_ACTIVITY_TABS = Object.freeze([
  'priority_messages',
  'new_posts',
  'subscriptions',
  'purchases_tips',
  'promotions',
  'archived',
]);

const EMPTY_TABS = Object.freeze([]);

/** Tab ids to prefetch in parallel (creator or subscriber) */
export const getFetchableActivityTabIds = (role) => {
  if (role === ROLE.CREATOR_SUB) {
    return CREATOR_SUB_ACTIVITY_TABS;
  }
  if (role === ROLE.CREATOR_CONTENT) {
    return CREATOR_CONTENT_ACTIVITY_TABS;
  }
  if (role === ROLE.USER) {
    return USER_ACTIVITY_TABS;
  }
  return EMPTY_TABS;
};

export const ACTIVITY_TAB_ICONS = {
  priority_messages: 'star',
  sales_tips: 'money-bill-wave',
  subscribers: 'calendar',
  followers: 'calendar',
  new_posts: 'photo-film',
  subscriptions: 'calendar',
  purchases_tips: 'money-bill-wave',
  promotions: 'gift',
  payouts: 'money-check-dollar',
  tags: 'tags',
  promotion_campaign: 'gift',
  archived: 'box-archive',
};


export const ACTIVITY_TITLE_MAP = {
  'subscription': 'Subscriptions',
  'purchases_tips': 'Purchases & Tips',
  'new_posts': 'New Posts',
  'followers': 'Followers',
  'payouts': 'Payouts',
  'tags': 'Tags',
  'promotion_campaign': 'Promotion Campaign',
  'archived': 'Archived',
  'sales_tips': 'Sales & Tips',
  'subscribers': 'Subscribers',
  'promotions': 'Promotions',
};

export const TITLES_WITH_INLINE_MENTIONS = new Set([
  ACTIVITY_TITLE_MAP.subscription,
  ACTIVITY_TITLE_MAP.purchases_tips,
  ACTIVITY_TITLE_MAP.sales_tips,
  ACTIVITY_TITLE_MAP.subscribers,
  ACTIVITY_TITLE_MAP.promotion_campaign,
  ACTIVITY_TITLE_MAP.followers,
  ACTIVITY_TITLE_MAP.payouts,
  ACTIVITY_TITLE_MAP.promotions,
  ACTIVITY_TITLE_MAP.archived,
]);
